import React from 'react';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
export function ProblemSolution() {
  const symptoms = ['Ganas "bien"… pero sabes que podrías ganar más.', 'Has invertido en libros, cursos o mentorías.', 'Te esfuerzas, cumples, haces lo correcto.', 'Y aun así… sientes culpa por querer más.'];
  const feelings = ['Incomodidad al cobrar mejor', 'Miedo a "cambiar"', 'Dudas cuando el dinero empieza a crecer', 'Como si una parte de ti dijera: "Sí, quiero prosperar… pero no quiero dejar de ser quien soy."'];
  return <>
      {/* The Problem */}
      <Section id="problem" background="white" data-id="element-184">
        <div className="max-w-3xl mx-auto" data-id="element-185">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-186">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-187">
              El conflicto silencioso que casi nadie nombra
            </h2>
            <p className="text-xl text-gray-600" data-id="element-188">Tal vez tú:</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-189">
            <div className="bg-gray-50 p-8 rounded-lg border-l-4 border-gray-300" data-id="element-190">
              <ul className="space-y-4" data-id="element-191">
                {symptoms.map((item, i) => <li key={i} className="flex items-start" data-id="element-192">
                    <CheckCircle2 className="w-5 h-5 text-gray-400 mt-1 mr-3 flex-shrink-0" data-id="element-193" />
                    <span className="text-gray-700" data-id="element-194">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg border-l-4 border-gray-300" data-id="element-195">
              <ul className="space-y-4" data-id="element-196">
                {feelings.map((item, i) => <li key={i} className="flex items-start" data-id="element-197">
                    <AlertCircle className="w-5 h-5 text-gray-400 mt-1 mr-3 flex-shrink-0" data-id="element-198" />
                    <span className="text-gray-700" data-id="element-199">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-[#FDFBF7] p-8 md:p-10 border border-[#D4AF37]/20 text-center rounded-xl shadow-sm" data-id="element-200">
            <h3 className="text-2xl font-serif font-bold text-[#D4AF37] mb-4" data-id="element-201">
              Ese conflicto interno no es falta de ambición. <br data-id="element-202" />
              Es lealtad cultural no consciente.
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-203">
              Nadie nos preparó para manejar "más". Muchas mujeres latinas son
              hoy las primeras en su familia con estudios, las primeras en ganar
              más dinero, las primeras en romper techos.
            </p>

            <p className="text-gray-700 mt-6 font-medium mb-4" data-id="element-204">
              Pero nadie les enseñó:
            </p>

            <ul className="text-left max-w-xl mx-auto space-y-3 mb-6" data-id="element-205">
              <li className="flex items-start text-gray-700" data-id="element-206">
                <span className="text-[#D4AF37] mr-3 mt-1" data-id="element-207">•</span>
                <span data-id="element-208">Cómo sentirse seguras con ese crecimiento</span>
              </li>
              <li className="flex items-start text-gray-700" data-id="element-209">
                <span className="text-[#D4AF37] mr-3 mt-1" data-id="element-210">•</span>
                <span data-id="element-211">Cómo prosperar sin culpa</span>
              </li>
              <li className="flex items-start text-gray-700" data-id="element-212">
                <span className="text-[#D4AF37] mr-3 mt-1" data-id="element-213">•</span>
                <span data-id="element-214">
                  Cómo expandirse sin sentir que traicionan sus raíces
                </span>
              </li>
            </ul>

            <p className="text-gray-800 font-bold text-lg mt-6 mb-3" data-id="element-215">
              Así que hacen lo único que pueden: 👉 Se frenan.
            </p>

            <p className="text-gray-700 italic" data-id="element-216">
              No porque no puedan. Sino porque no tienen permiso interno.
            </p>
          </motion.div>

          <div className="flex justify-center mt-8" data-id="element-217">
            <Button variant="primary" size="lg" withArrow onClick={() => document.getElementById('offer')?.scrollIntoView({
            behavior: 'smooth'
          })} data-noscript="true" data-id="element-218">
              Quiero Reprogramar Mi Mente
            </Button>
          </div>
        </div>
      </Section>

      {/* The Root Cause */}
      <Section background="black" className="text-white" data-id="element-219">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-220">
          <div data-id="element-221">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-222">
              El problema no es el dinero. <br data-id="element-223" />
              <span className="text-[#D4AF37]" data-id="element-224">
                Es la relación que aprendiste a tener con él.
              </span>
            </h2>
            <p className="text-gray-300 mb-6 text-lg" data-id="element-225">
              Tal vez creciste escuchando cosas como:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-226">
              {['"El dinero cambia a la gente"', '"Mejor poco pero honrado"', '"No presumas"', '"Conformarse es humildad"'].map((quote, i) => <li key={i} className="flex items-center text-gray-200 italic" data-id="element-227">
                  <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3" data-id="element-228"></span>
                  {quote}
                </li>)}
            </ul>
            <p className="text-gray-300" data-id="element-229">
              Nada de eso venía del mal. Venía de supervivencia. Pero hoy tú no
              estás sobreviviendo. Estás expandiéndote. Y una mente entrenada
              para sobrevivir no sabe qué hacer cuando empieza a prosperar.
            </p>
          </div>
          <div className="relative" data-id="element-230">
            <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/20 to-transparent rounded-lg transform rotate-3" data-id="element-231"></div>
            <div className="bg-gray-900 p-8 rounded-lg border border-gray-800 relative z-10" data-id="element-232">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-233">
                Por eso otras soluciones no te funcionaron
              </h3>
              <div className="space-y-4" data-id="element-234">
                {['Presupuestos que abandonaste', 'Libros de finanzas fríos y técnicos', 'Frases motivacionales vacías', 'Cursos que te decían "piensa positivo"'].map((fail, i) => <div key={i} className="flex items-center p-3 bg-black/50 rounded border border-gray-800" data-id="element-235">
                    <XCircle className="w-5 h-5 text-red-400 mr-3" data-id="element-236" />
                    <span className="text-gray-300" data-id="element-237">{fail}</span>
                  </div>)}
              </div>
              <p className="mt-6 mb-3 text-white font-semibold text-lg" data-id="element-238">
                ¿Y sabes por qué no funcionaron?
              </p>
              <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded text-center" data-id="element-239">
                <p className="text-[#D4AF37] font-medium" data-id="element-240">
                  Atacan el qué hacer, pero ignoran el quién estás siendo.
                </p>
              </div>

              <div className="mt-6 text-gray-300 space-y-4" data-id="element-241">
                <p className="font-medium" data-id="element-242">
                  Y mientras tu identidad financiera no cambie, el resultado
                  siempre vuelve al mismo lugar.
                </p>
                <p className="text-center italic text-gray-400" data-id="element-243">Es como:</p>
                <p className="text-center font-serif text-lg text-white" data-id="element-244">
                  Pintar una casa hermosa sobre cimientos viejos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* The Solution Setup */}
      <Section id="solution" background="cream" data-id="element-245">
        <div className="max-w-4xl mx-auto text-center" data-id="element-246">
          <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-247">
            Aquí está la verdad que cambia todo
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-248">
            {['No necesitas volverte alguien distinta', 'No necesitas dejar de ser humilde', 'No necesitas volverte materialista'].map((truth, i) => <div key={i} className="bg-white p-6 rounded-lg shadow-sm border-t-4 border-[#D4AF37]" data-id="element-249">
                <p className="font-medium text-gray-800" data-id="element-250">{truth}</p>
              </div>)}
          </div>

          <p className="text-2xl font-serif text-gray-800 mb-8" data-id="element-251">
            Necesitas reprogramar la historia que tu mente cuenta sobre el
            dinero.
          </p>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12" data-id="element-252">
            Porque el dinero no responde a esfuerzo. Responde a identidad. La
            pieza que faltaba es el <strong data-id="element-253">permiso interno</strong>.
          </p>

          <div className="bg-white p-8 rounded-xl shadow-lg inline-block text-left max-w-2xl w-full" data-id="element-254">
            <h3 className="text-xl font-bold mb-4 text-center" data-id="element-255">
              Permiso para:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-256">
              {['Ganar más', 'Cobrar lo que valen', 'Tomar decisiones grandes', 'Vivir con calma', 'Ser ejemplo para sus hijos'].map((perm, i) => <div key={i} className="flex items-center" data-id="element-257">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mr-2" data-id="element-258" />
                  <span data-id="element-259">{perm}</span>
                </div>)}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-100 text-center" data-id="element-260">
              <p className="text-xl font-serif italic text-[#D4AF37]" data-id="element-261">
                "Prosperar no contradice tus valores. Los expande."
              </p>
            </div>
          </div>
        </div>
      </Section>
    </>;
}